import { Link } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import type { ProviderType } from "@wags/catalog/pure.ts";
import { ChevronSmallRightIcon } from "@wags/ui/product-icons.tsx";
import type { MoreDogsSurface } from "./more-dogs-analytics";
import { FamilyRow } from "./family-row";
import { ProviderAvatar } from "./provider-avatar";
import type { ProviderOtherDogs } from "./use-provider-other-dogs";
import { VerifiedProviderBadge } from "@/components/shared/VerifiedProviderBadge";

export type MoreDogsProvider = {
    id: string;
    name: string;
    place: string | undefined;
    avatarUrl: string | undefined;
    isVerified: boolean;
    type: ProviderType;
};

export function MoreDogsBreederCard({
    provider,
    otherDogs,
    current,
    sourceListingId,
    surface,
}: {
    provider: MoreDogsProvider;
    otherDogs: ProviderOtherDogs;
    current: ComponentProps<typeof FamilyRow>["current"];
    sourceListingId: string;
    surface: MoreDogsSurface;
}) {
    return (
        <div className="rounded-nested bg-doberman-black/[0.03] p-1">
            <Link
                to="/provider/$providerId"
                params={{ providerId: provider.id }}
                className="flex items-center gap-3 rounded-sm px-3 py-2.5 transition-colors duration-150 active:bg-doberman-black/[0.04] [@media(hover:hover)]:hover:bg-doberman-black/[0.03] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-labrador-yellow"
            >
                <ProviderAvatar url={provider.avatarUrl} />
                <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex min-w-0 items-baseline text-label text-doberman-black">
                        <span className="truncate">{provider.name}</span>
                        <VerifiedProviderBadge
                            isVerified={provider.isVerified}
                            providerType={provider.type}
                        />
                    </span>
                    {provider.place && (
                        <span className="truncate text-caption text-doberman-black/55">
                            {provider.place}
                        </span>
                    )}
                </span>
                <ChevronSmallRightIcon
                    aria-hidden="true"
                    className="size-5 shrink-0 text-doberman-black/40"
                />
            </Link>
            <FamilyRow
                key={sourceListingId}
                otherDogs={otherDogs}
                providerId={provider.id}
                sourceListingId={sourceListingId}
                surface={surface}
                current={current}
            />
        </div>
    );
}
